import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import type { PlanCoordinates } from '../types';

/**
 * Die ANONYME Melderseite `#/freimeldung/<token>` — nur RPCs, keine Tabelle
 * (docs/freimeldung-online-plan.md §B, §E). `anon` hat auf
 * `report_freimeldung_links` und `inspection_item_freimeldungen` keinerlei
 * Rechte; alles laeuft ueber `security definer`-Funktionen, die den Token
 * pruefen (Muster sigeEinweisungPublicService.ts / supabase/
 * report_freimeldung_rpc.sql, Nr. 109).
 *
 * RPC-Namen und Parameter EXAKT wie im SQL. Wer hier einen Parameter
 * umbenennt, bricht die Seite still: die Datenbank antwortet dann mit PGRST202
 * „could not find the function".
 *
 * Die oeffentliche RPC liefert NIE Feststellung, Vorschrift, Fristverlauf,
 * geschlossene Maengel oder Kontaktdaten der Beteiligten — und dieses Modul
 * nimmt auch nichts davon entgegen.
 */

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => {
  if (v == null) return null;
  const s = str(v).trim();
  return s ? s : null;
};
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** Erster gesetzter Wert unter mehreren Schluesseln — die RPC darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
  for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  return undefined;
};

/**
 * Fehler mit dem SQLSTATE der RPC (`P0002` = Link ungueltig/abgelaufen,
 * `P0001` = Mangel inzwischen erledigt, `54000` = Mengenbremse, `22023` =
 * Eingabe abgelehnt). Die Seite entscheidet daran, was sie sagt — der Klartext
 * allein ist fuer Menschen.
 */
export class FreimeldungRpcFehler extends Error {
  code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'FreimeldungRpcFehler';
    this.code = code;
  }
}

/** SQLSTATE eines Fehlers, wenn er einen traegt (Supabase-Fehlerobjekt oder FreimeldungRpcFehler). */
export const freimeldungFehlerCode = (e: unknown): string | null => {
  const code = (e as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code ? code : null;
};

const werfe = (e: unknown): never => {
  throw new FreimeldungRpcFehler(describeError(e), freimeldungFehlerCode(e));
};

/**
 * Migration Nr. 109 noch nicht gefahren: PostgREST kennt die Funktion nicht
 * (PGRST202). Fuer den anonymen Besucher ist das dasselbe wie ein ungueltiger
 * Link — er bekommt keine Diagnose.
 */
const istFehlendeFunktion = (e: unknown): boolean => {
  const code = freimeldungFehlerCode(e);
  const msg = typeof e === 'object' && e !== null ? str((e as { message?: unknown }).message) : '';
  return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(msg);
};

/** jsonb-Antwort einer RPC als Objekt; Zeichenketten werden geparst, alles andere ist null. */
const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

// ─── Das Blatt ──────────────────────────────────────────────────────────────

/** Ein offener Mangel, so wie ihn der Melder sieht — ohne Feststellung und Vorschrift. */
export interface FreimeldungPublicItem {
  id: string;
  ticketNumber: number | null;
  title: string;
  location: string;
  responsiblePerson: string;
  deadline: string | null;
  immediateAction: boolean;
  /** Erstes Foto des Mangels, nur wenn es oeffentlich abrufbar ist. */
  fotoUrl: string | null;
  /** Wie viele offene Meldungen schon zu diesem Mangel vorliegen. */
  offeneMeldungen: number;
  /**
   * Nadeln auf den Projektplaenen (Migration Nr. 182): Plan des Eintrags und
   * die Nadeln in Plan-Prozent, gelesen NUR ueber services/planPins.ts.
   * Fehlen beide = keine Nadel oder Plaene nicht freigegeben.
   */
  planId?: string;
  planCoordinates?: PlanCoordinates[];
}

/** Ein Plan, auf dem ein Mangel dieses Links eine Nadel hat (Nr. 182). */
export interface FreimeldungPublicPlan {
  id: string;
  name: string;
  /** Oeffentliche Bildadresse, immer https. */
  bildUrl: string;
}

export interface FreimeldungPublic {
  projektName: string;
  projektAdresse: string;
  bueroName: string;
  berichtNr: number;
  berichtDatum: string | null;
  sprache: 'de' | 'en';
  /** Beweisfoto verpflichtend? Pro Projekt einstellbar, Vorgabe optional. */
  fotoPflicht: boolean;
  /** Vorschlagsliste der Firmen aus den Zustaendigkeiten des Berichts. */
  firmen: string[];
  items: FreimeldungPublicItem[];
  /** Plaene zu den Nadeln; leer ohne Nr. 182 oder wenn das PDF keine Plaene druckt. */
  plaene: FreimeldungPublicPlan[];
}

const istProzent = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100;

/**
 * Nadeln aus der RPC — streng: nur x/y als Zahl 0–100 und eine planId als
 * Text, alles andere faellt weg. Einzelobjekt (Altstand) wird ein Element.
 */
const mapNadeln = (roh: unknown): PlanCoordinates[] | undefined => {
  const liste: unknown[] = Array.isArray(roh) ? roh : roh && typeof roh === 'object' ? [roh] : [];
  const nadeln = liste.flatMap((eintrag): PlanCoordinates[] => {
    if (!eintrag || typeof eintrag !== 'object') return [];
    const c = eintrag as Zeile;
    if (!istProzent(c.x) || !istProzent(c.y)) return [];
    const planId = typeof c.planId === 'string' && c.planId.trim() ? c.planId : undefined;
    return [planId ? { x: c.x, y: c.y, planId } : { x: c.x, y: c.y }];
  });
  return nadeln.length > 0 ? nadeln : undefined;
};

const mapPlan = (row: Zeile): FreimeldungPublicPlan | null => {
  const id = str(pick(row, 'id')).trim();
  const bildUrl = str(pick(row, 'bildUrl', 'bild_url')).trim();
  if (!id || !/^https:\/\//i.test(bildUrl)) return null;
  return { id, name: str(pick(row, 'name')).trim(), bildUrl };
};

const mapItem = (row: Zeile): FreimeldungPublicItem => {
  const item: FreimeldungPublicItem = {
    id: str(pick(row, 'id')),
    ticketNumber: numOrNull(pick(row, 'ticketNumber', 'ticket_number')),
    title: str(pick(row, 'title')),
    location: str(pick(row, 'location')),
    responsiblePerson: str(pick(row, 'responsiblePerson', 'responsible_person')),
    deadline: strOrNull(pick(row, 'deadline')),
    immediateAction: pick(row, 'immediateAction', 'immediate_action') === true,
    fotoUrl: strOrNull(pick(row, 'fotoUrl', 'foto_url')),
    offeneMeldungen: num(pick(row, 'offeneMeldungen', 'offene_meldungen'), 0),
  };
  const planId = strOrNull(pick(row, 'planId', 'plan_id'));
  const planCoordinates = mapNadeln(pick(row, 'planCoordinates', 'plan_coordinates'));
  if (planId) item.planId = planId;
  if (planCoordinates) item.planCoordinates = planCoordinates;
  return item;
};

export const mapFreimeldungPublic = (row: Zeile): FreimeldungPublic => ({
  projektName: str(pick(row, 'projektName', 'projekt_name', 'project_name')),
  projektAdresse: str(pick(row, 'projektAdresse', 'projekt_adresse', 'project_address')),
  bueroName: str(pick(row, 'bueroName', 'buero_name', 'company_name')),
  berichtNr: num(pick(row, 'berichtNr', 'bericht_nr', 'inspection_number'), 0),
  berichtDatum: strOrNull(pick(row, 'berichtDatum', 'bericht_datum', 'report_date')),
  sprache: str(pick(row, 'sprache')) === 'en' ? 'en' : 'de',
  fotoPflicht: pick(row, 'fotoPflicht', 'foto_pflicht') === true,
  firmen: arr<unknown>(pick(row, 'firmen'))
    .map((x) => str(x).trim())
    .filter(Boolean),
  items: arr<unknown>(pick(row, 'items'))
    .filter((i): i is Zeile => !!i && typeof i === 'object')
    .map(mapItem),
  plaene: arr<unknown>(pick(row, 'plaene'))
    .filter((p): p is Zeile => !!p && typeof p === 'object')
    .map(mapPlan)
    .filter((p): p is FreimeldungPublicPlan => p !== null),
});

/**
 * Das Blatt zum Link (RPC `report_freimeldung_public`). `null`, wenn der Token
 * unbekannt oder der Link abgelaufen ist — ODER die Migration noch fehlt. Die
 * Seite sagt in allen Faellen „Dieser Link ist nicht mehr gültig" und verraet
 * nicht, ob es den Bericht gibt.
 */
export const ladeFreimeldungPublic = async (token: string): Promise<FreimeldungPublic | null> => {
  const t = (token || '').trim();
  if (!t) return null;
  try {
    const { data, error } = await supabase.rpc('report_freimeldung_public', { p_token: t });
    if (error) throw error;
    const row = alsObjekt(data);
    return row ? mapFreimeldungPublic(row) : null;
  } catch (e) {
    if (istFehlendeFunktion(e)) return null;
    return werfe(e);
  }
};

// ─── Melden ─────────────────────────────────────────────────────────────────

export interface FreimeldungEingabe {
  itemId: string;
  firma: string;
  name: string;
  email?: string;
  phone?: string;
  /** Bei einer Zurueckweisung die Begruendung (Pflicht, siehe Nr. 185). */
  kommentar?: string;
  /** Oeffentliche URL aus `ladeFreimeldungFotoHoch`, nie eine DataURL. */
  fotoUrl?: string | null;
  /**
   * `zurueckgewiesen` = die Firma weist den Mangel mit Begruendung zurueck
   * (RPC `report_freimeldung_zurueckweisen`, Nr. 185). Fehlt der Wert: eine
   * Freimeldung ueber `report_freimeldung_melden`, wie bisher.
   */
  art?: 'behoben' | 'zurueckgewiesen';
}

export interface FreimeldungErgebnis {
  ok: boolean;
  /** true = dieselbe Firma/Person hatte diesen Mangel schon gemeldet; es wurde NICHTS geschrieben. */
  bereits: boolean;
  freimeldungId: string;
  gemeldetAt: string;
}

/**
 * Meldet einen Mangel als behoben (RPC `report_freimeldung_melden`).
 *
 * Die Meldung SCHLIESST den Mangel NICHT — sie legt eine offene Freimeldung
 * an, ueber die der Koordinator entscheidet.
 *
 * Die Datenbank prueft Link, Mengenbremse, Zugehoerigkeit des Mangels, Laengen
 * und die Foto-Pflicht und schreibt `on conflict … do nothing` — eine zweite
 * Meldung derselben Person ist kein Fehler, sondern `bereits: true`.
 *
 * Fehler kommen als FreimeldungRpcFehler mit SQLSTATE und Klartext durch
 * (P0002 Link ungueltig, P0001 Mangel inzwischen erledigt, 54000 zu viele,
 * 22023 Eingabe).
 */
export const meldeFreimeldung = async (
  token: string,
  eingabe: FreimeldungEingabe,
): Promise<FreimeldungErgebnis> => {
  const t = (token || '').trim();
  if (!t) throw new FreimeldungRpcFehler('Dieser Link ist nicht gültig.', 'P0002');
  if (!(eingabe?.itemId || '').trim()) {
    throw new FreimeldungRpcFehler('Bitte zuerst einen Mangel auswählen.', '22023');
  }

  const ua = (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '').slice(0, 500);

  const gemeinsam = {
    p_token: t,
    p_item_id: (eingabe.itemId || '').trim(),
    p_firma: (eingabe.firma || '').trim(),
    p_name: (eingabe.name || '').trim(),
    // Leer geht als NULL — die Spalten sind optional, '' waere ein leerer Text.
    p_email: (eingabe.email || '').trim() || null,
    p_phone: (eingabe.phone || '').trim() || null,
    p_foto_url: (eingabe.fotoUrl || '').trim() || null,
    p_user_agent: ua || null,
  };
  // Zwei RPCs statt eines Schalters an `melden`: die Signatur von `melden`
  // bleibt, wie sie ist — eine noch offene alte Seite meldet weiter.
  const { data, error } = eingabe.art === 'zurueckgewiesen'
    ? await supabase.rpc('report_freimeldung_zurueckweisen', {
        ...gemeinsam,
        p_begruendung: (eingabe.kommentar || '').trim() || null,
      })
    : await supabase.rpc('report_freimeldung_melden', {
        ...gemeinsam,
        p_kommentar: (eingabe.kommentar || '').trim() || null,
      });
  if (error) return werfe(error);

  const row = alsObjekt(data);
  if (!row) throw new Error('Die Meldung wurde nicht bestätigt — bitte erneut versuchen.');
  return {
    ok: pick(row, 'ok') !== false,
    bereits: pick(row, 'bereits') === true,
    freimeldungId: str(pick(row, 'freimeldungId', 'freimeldung_id')),
    gemeldetAt: str(pick(row, 'gemeldetAt', 'gemeldet_at')),
  };
};

// ─── Foto ───────────────────────────────────────────────────────────────────

/**
 * `POST /api/freimeldung-foto` — der einzige Weg, ein Beweisfoto abzulegen:
 * `anon` hat keine Storage-Rechte, der Server prueft den Token ueber
 * `report_freimeldung_foto_pfad` (nur service_role) und legt die Datei mit dem
 * Service-Role-Key ab. Der Browser sieht nie einen Storage-Pfad.
 *
 * Erwartet eine JPEG-DataURL (der Client re-kodiert mit `compressImage`) und
 * liefert die oeffentliche URL zurueck.
 *
 * Der Rumpf wird erst als Text gelesen und dann geparst: Faellt die Route auf
 * eine HTML-Fehlerseite zurueck (502 von Vercel), wuerde `response.json()`
 * werfen und die Ursache waere verloren.
 */
export const ladeFreimeldungFotoHoch = async (token: string, dataUrl: string): Promise<string> => {
  const t = (token || '').trim();
  if (!t) throw new Error('Dieser Link ist nicht gültig.');
  if (!(dataUrl || '').startsWith('data:image/')) throw new Error('Bitte ein Foto auswählen.');

  let antwort: Response;
  try {
    antwort = await fetch('/api/freimeldung-foto', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: t, dataUrl }),
    });
  } catch (e) {
    throw new Error(`Der Server ist nicht erreichbar (${describeError(e)}).`);
  }

  const roh = await antwort.text().catch(() => '');
  let body: Zeile = {};
  try {
    const parsed = roh ? JSON.parse(roh) : null;
    if (parsed && typeof parsed === 'object') body = parsed as Zeile;
  } catch {
    body = {};
  }

  if (!antwort.ok) {
    const text = strOrNull(pick(body, 'error'));
    throw new Error(text || `Das Foto wurde abgelehnt (HTTP ${antwort.status}).`);
  }
  const url = strOrNull(pick(body, 'url'));
  if (!url) throw new Error('Der Server hat keine Adresse für das Foto geliefert.');
  return url;
};
